import React from 'react';
import type { DriveItem } from '../types';
import { formatDate } from '../lib/drive-items';
import { ProtectedImage } from '@/components/shared/ProtectedImage';
import { DriveItemIcon, DriveItemMenu, SelectAllCheckbox, type DriveItemActions } from './DriveItemParts';

interface DriveThumbnailsProps {
  items: DriveItem[];
  selectedItems: string[];
  onSelect: (itemId: string, isSelected: boolean) => void;
  onSelectAll: (all: boolean) => void;
  actions: DriveItemActions;
}

const isImage = (item: DriveItem) => item.type === 'file' && !!item.download_url && !!item.mime_type?.startsWith('image/');

/**
 * Thumbnails: images show themselves, loaded through KrewUp a few at a time (the grid renders at once and each picture fills in); folders, links and other
 * files show a quiet icon.
 */
export function DriveThumbnails({ items, selectedItems, onSelect, onSelectAll, actions }: DriveThumbnailsProps) {
  const selected = new Set(selectedItems);
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 font-table-body-muted w-fit">
        <SelectAllCheckbox total={items.length} selected={items.filter((i) => selected.has(i.id)).length} onChange={onSelectAll} />
        Select all
      </label>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))' }}>
        {items.map((item) => (
            <div
              key={item.id}
              className={`group relative border rounded-lg bg-white overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors ${selected.has(item.id) ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
              onClick={() => actions.onOpen(item)}
            >
              <div className="h-28 flex items-center justify-center bg-gray-50 border-b border-gray-100">
                {isImage(item) ? (
                  <ProtectedImage kind="drive" id={item.id} alt={item.name} className="h-full w-full object-cover" fallback={<DriveItemIcon item={item} className="w-10 h-10 text-gray-300" />} />
                ) : (
                  <DriveItemIcon item={item} className="w-10 h-10 text-gray-300" />
                )}
              </div>
              <div className="px-3 py-2">
                <div className="font-table-body truncate" title={item.name}>{item.name}</div>
                <div className="font-table-body-muted">{formatDate(item.updated_at)}</div>
              </div>
              <div className="absolute top-2 left-2 bg-white rounded p-0.5" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  aria-label={`Select ${item.name}`}
                  checked={selected.has(item.id)}
                  onChange={(e) => onSelect(item.id, e.target.checked)}
                  className="h-3.5 w-3.5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                />
              </div>
              <div className="absolute top-1.5 right-1.5">
                <DriveItemMenu item={item} actions={actions} />
              </div>
            </div>
        ))}
      </div>
    </div>
  );
}
